import { QueryLazyOptions } from '@apollo/client';
import React, { useCallback, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import {
  Cell,
  Column,
  Row,
  SortingRule,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';

import { GridFooter, GridGlobalFilter, GridHeader } from '..';
import { useWhyDidYouUpdate } from '../../hooks';

import ResizableTable from './resizable-table/resizable-table.js';

const defTableProps = {
  striped: true,
  hover: true,
  bordered: true,
  className: 'mb-0',
};
const defHeaderGroupProps = () => ({ className: 'text-center' });
const defHeaderProps = () => ({ className: 'align-middle' });
const defFooterGroupProps = () => ({ className: 'text-center' });
const defFooterProps = () => ({ className: 'align-middle' });

// eslint-disable-off-next-line-off functional/no-mixed-type, @typescript-eslint/ban-types
type Props<D extends object = {}> = {
  readonly id?: string;
  readonly appStore?: any;
  readonly title?: string;
  readonly columns: ReadonlyArray<Column<D>>;
  readonly data: readonly D[];
  readonly loading?: boolean;
  readonly propPageSize?: number;
  readonly initPageIndex?: number;
  readonly get?: (options?: QueryLazyOptions<any>) => void;
  readonly filterVariables?: Record<string, any>;
  readonly hasFooter?: boolean;
  readonly hasGridFooter?: boolean;
  readonly hasSearch?: boolean;
  readonly hasPdf?: any;
  readonly tableProps?: object;
  readonly bodyProps?: object;
  readonly headerGroupProps?: (headerGroup) => object;
  readonly footerGroupProps?: (footerGroup) => object;
  readonly headerProps?: (column: Column<D>) => object;
  readonly footerProps?: (column: Column<D>) => object;
  readonly rowProps?: (row: Row<D>) => object;
  readonly cellProps?: (cell: Cell<D>) => object;
  readonly onSelectedRowsChange?: (rows: ReadonlyArray<Row<D>>) => void;
  readonly onPageChange?: (page: number) => void;
  readonly onSortByChange?: (sort: readonly SortingRule<D>[]) => void;
  readonly onRefresh?: () => void;
  readonly onCreate?: () => void;
  readonly onFileUpload?: any;
  readonly onExport?: () => void;
  readonly onNew?: () => void;
  readonly resizable?: boolean;
};

function TableInMem<D extends object = {}>({
  title = '',
  appStore,
  columns,
  data,
  loading,
  propPageSize = null,
  initPageIndex = 0,
  get = null,
  filterVariables = {},
  hasFooter = false,
  hasGridFooter = true,
  hasSearch = false,
  hasPdf = null,
  onSelectedRowsChange = null,
  onPageChange = null,
  onSortByChange = null,
  onRefresh = null,
  onCreate = null,
  onFileUpload = null,
  onExport = null,
  onNew = null,
  tableProps = defTableProps,
  headerGroupProps = defHeaderGroupProps,
  headerProps = defHeaderProps,
  footerGroupProps = defFooterGroupProps,
  footerProps = defFooterProps,
  bodyProps = null,
  rowProps = null,
  cellProps = null,
  resizable = false,
}: Props<D>) {
  //
  //-------------------- Body -----------------------------------------------
  //
  const actualPageSize = propPageSize ? propPageSize : appStore.pageSize;
  const controlledPageCount = ~~((data.length - 1) / actualPageSize) + 1;
  /**
   * * useTable : Main Table Machine
   */
  const tableInstance = useTable<D>(
    {
      columns,
      data,
      initialState: { pageIndex: initPageIndex, pageSize: actualPageSize }, // Pass our hoisted table state
      pageCount: controlledPageCount,
    },
    useGlobalFilter, // useGlobalFilter!
    useSortBy,
    usePagination,
    useRowSelect,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    prepareRow,
    page,
    rows,
    pageCount,
    gotoPage,
    setPageSize,
    selectedFlatRows,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = tableInstance;

  /**
   * * 1. Effect : ΕΚΤΕΛΕΙ ΤΟ get αν υπάρχει
   */
  useEffect(() => {
    const variables = { ...filterVariables };
    if (get !== null) {
      console.log('[TableInMem] Effect Query:', variables);
      get({ variables });
    }
  }, [filterVariables, get]);

  /**
   * * 2. Effect : ΕΚΤΕΛΕΙ ΤΟ gotoPage οταν Αλλάξει το initPageIndex
   */
  useEffect(() => {
    gotoPage(initPageIndex);
  }, [gotoPage, initPageIndex]);

  /**
   * * 3. Effect : ΕΚΤΕΛΕΙΤΑΙ όταν αλλάξει το selectedFlatRows
   */
  useEffect(() => {
    onSelectedRowsChange && onSelectedRowsChange(selectedFlatRows);
  }, [onSelectedRowsChange, selectedFlatRows]);

  /**
   * * 4. Effect : ΕΚΤΕΛΕΙΤΑΙ όταν αλλάξει το sortBy (Δεν χρειάζεται στο mem)
   */
  //useEffect(() => {
  //console.log('[TableInMem] onSortByChange', sortBy);
  //onSortByChange && onSortByChange(sortBy);
  //}, [onSortByChange, sortBy]);

  //
  //-------------------- Handlers -------------------------------------------
  //
  const handlePageChange = useCallback(
    (pageNo) => {
      gotoPage(pageNo);
      onPageChange && onPageChange(pageNo);
    },
    [gotoPage, onPageChange],
  );

  const handlePageSizeChange = useCallback(
    (n: number) => {
      appStore.setPageSize(n);
      setPageSize(n);
    },
    [appStore, setPageSize],
  );
  //
  //-------------------- Render ----------------------------------------------
  //
  useWhyDidYouUpdate('[TableInMem]', {
    get,
    filterVariables,
    onSelectedRowsChange,
    selectedFlatRows,
  });
  return (
    <Card className="shadow table-container">
      {title !== '' && (
        <GridHeader
          title={title}
          onRefresh={onRefresh}
          onCreate={onCreate}
          onFileUpload={onFileUpload}
        />
      )}
      <Card.Body className="py-4">
        {hasSearch && <GridGlobalFilter value={globalFilter} onChange={setGlobalFilter} />}
        {/* Το LoadingOverlay έχει default active=true· χρειάζεται ρητό boolean */}
        <LoadingOverlay
          active={Boolean(loading) || (get != null && (data === undefined || data === null))}
          spinner
          text="Παρακαλώ περιμένετε..."
        >
          <ResizableTable
            id
            resizable={resizable}
            resizeOptions={{}}
            {...getTableProps(tableProps || {})}
          >
            <thead className="thead-dark">
              {headerGroups.map((headerGroup) => (
                <tr
                  {...headerGroup.getHeaderGroupProps(
                    (headerGroupProps && headerGroupProps(headerGroup)) || {},
                  )}
                >
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(
                        column.getSortByToggleProps((headerProps && headerProps(column)) || {}),
                      )}
                    >
                      <span>
                        {column.render('Header')}
                        {/* Add a sort direction indicator */}
                        {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps(bodyProps || {})}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps((rowProps && rowProps(row)) || {})}>
                    {row.cells.map((cell) => {
                      /* width on columns */
                      const lcWidthProps =
                        typeof cell?.column?.width === 'string' && cell.column.width
                          ? { width: cell.column.width }
                          : {};

                      const lcCellProps = cell.getCellProps((cellProps && cellProps(cell)) || {});
                      const lcProps = { ...lcWidthProps, ...lcCellProps };
                      //console.log('$$$', typeof cell?.column?.width, cell?.column?.width, lcProps);

                      return <td {...lcProps}>{cell.render('Cell')}</td>;
                      //return <td {...cell.getCellProps((cellProps && cellProps(cell)) || {})}>{cell.render('Cell')}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
            {hasFooter && (
              <tfoot>
                {footerGroups.map((footerGroup) => (
                  <tr
                    {...footerGroup.getFooterGroupProps(
                      (footerGroupProps && footerGroupProps(footerGroup)) || {},
                    )}
                  >
                    {footerGroup.headers.map((column) => {
                      const footerCellProps = column.getFooterProps();
                      return (
                        <td
                          {...footerCellProps}
                          style={{
                            ...footerCellProps.style,
                            // @ts-expect-error Bootstrap table CSS variables
                            '--bs-table-bg': '#e1f3f7',
                            '--bs-table-accent-bg': '#e1f3f7',
                            backgroundColor: '#e1f3f7',
                            boxShadow: 'none',
                          }}
                        >
                          {column.render('Footer')}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tfoot>
            )}
          </ResizableTable>
        </LoadingOverlay>
      </Card.Body>
      {hasGridFooter && (
        <GridFooter
          hasPdf={hasPdf}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          recordCount={rows.length}
          onPageChange={handlePageChange}
          onPageSizeChange={propPageSize ? null : handlePageSizeChange}
          onExport={!!onExport && onExport}
          onNew={!!onNew && onNew}
        />
      )}
    </Card>
  );
}

export default TableInMem;
